import * as React from 'react';
import styled from 'styled-components';
import { ensureArray } from '@agilepoint/utils-array';
import { memoizeFunction } from '@agilepoint/utils-common';
import { isDateInRange } from '@agilepoint/utils-date-time';
import { ICompactTimePickerProps } from './compact-time-picker-props';
import { formatTimeInRange, Picker } from './picker';
import { timeValidator } from './time-validator';
import { StyledCallout, StyledCalloutContent } from './parts';
import { parseTime } from './utils';
import { Textbox } from '../textbox';
import { Typography } from '../typography';

interface ICompactTimePickerState {
    time: string;
    isCallOutVisible: boolean;
}

interface ITime {
    hour: string;
    minute: string;
}

interface ITimeFormatOptions {
    is12Hour?: boolean;
    timeSeperator: string;
}

const KEYS_TO_LISTEN: { [key: string]: number } = {
    spaceBar: 32,
    enter: 13
};

const regex = /\d+|\w+/g;

const Wrapper = styled.div`
    display: inline-block;
    width: 240px;
    height: 40px;
    
    ${Textbox} {
        width: 100%;
        height: 100%;
    }
`;

const getOptions = memoizeFunction((is12Hour?: boolean, interval = 60): string[] => {
    const timing: string[] = [];
    let hours;
    let minutes;
    let i = 0;
    while (i < 24 * 60) {
        hours = Math.floor(i / 60);
        minutes = i % 60;
        if (minutes < 10) {
            minutes = '0' + minutes;
        }

        const amPm = is12Hour ? getValueForAmPmPicker(hours) : '';

        const isShow12Hour = is12Hour ? 12 : 24;
        hours %= isShow12Hour;

        if (is12Hour && hours === 0) {
            hours = 12;
        }

        if (hours < 10) {
            hours = '0' + hours;
        }

        timing.push((hours + ':' + minutes + ' ' + amPm).trim());
        i += interval;
    }
    return timing;
});

function getValueForAmPmPicker(hours: number) {
    const amPm = hours < 12 ? 'AM' : 'PM';
    return amPm;
}

function getTimeAsString(time: ITime, options: ITimeFormatOptions): string {
    const { hour } = time;
    const { is12Hour } = options;
    const newHour = is12Hour ? format12Hours(hour) : format24Hours(time);
    const newMinute = formatMinute(time);
    const newAmPm = formatAmPm(time, options);
    return formatTime(options, newHour, newMinute, newAmPm);
}

function format12Hours(hour: string) {
    const intHour = Number(hour);
    const subtractedHour = intHour - 12;
    return intHour < 10 && hour.indexOf('0') ? '0' + hour :
        intHour >= 12 ? subtractedHour < 10 ? '0' + subtractedHour : subtractedHour :
            hour;
}

function format24Hours(time: ITime) {
    const { hour, minute = '00' } = time;
    const intHour = Number(hour);

    return (
        minute.toLowerCase().includes('pm') && intHour < 12 ?
            hour === '12' ? intHour : intHour + 12 :
            minute.toLowerCase().includes('am') && hour === '12' ? '00' :
                hour.indexOf('0') && intHour < 10 ? '0' + hour :
                    hour === '0' ? '0' + hour : hour
    );
}

function formatMinute(time: ITime) {
    const { minute = '00' } = time;
    const splittedMinute = minute.match(regex);
    return splittedMinute ? splittedMinute[0] : minute;
}

function formatAmPm(time: ITime, options: ITimeFormatOptions) {
    const { hour, minute = '00' } = time;
    const { is12Hour } = options;
    const splittedMinute = minute.match(regex);
    const newAmPm = splittedMinute && splittedMinute[1] ? splittedMinute[1].toUpperCase() : is12Hour && Number(hour) < 12 ? 'AM' : 'PM';
    return newAmPm;
}

function formatTime(options: ITimeFormatOptions, hour: string | number, minute: string, amPm: string) {
    const { is12Hour, timeSeperator = ':' } = options;

    return (
        `${(is12Hour && hour === '00') ? '12' :
            hour || (is12Hour ? '01' : '00')}${timeSeperator}${minute.indexOf('0') &&
                Number(minute) < 10 ? '0' + minute : minute || '00'}${is12Hour ? ' ' + (amPm) : ''}`
    );
}

function isTimeOutOfRange(minTime: string, maxTime: string, time: string) {
    const { formattedMinDateTime, formattedMaxDateTime } = formatTimeInRange(minTime, maxTime);

    const formattedDate = parseTime(time);
    return !isDateInRange(formattedDate, formattedMinDateTime, formattedMaxDateTime);
}

function getInitialState(props: ICompactTimePickerProps) {
    const { value, is12Hour } = props;

    if (value) {
        const timeSeperator = ':';
        const [hour, minute] = value.split(timeSeperator);
        const time = getTimeAsString({ hour, minute }, { is12Hour, timeSeperator });
        const isValid = timeValidator.isValidTime(time, is12Hour, timeSeperator!);

        if (isValid) {
            return {
                isCallOutVisible: false,
                time
            };
        }
    }

    return {
        isCallOutVisible: false,
        time: ''
    };
}

export class CompactTimePicker extends React.PureComponent<ICompactTimePickerProps, ICompactTimePickerState> {

    private rootElementRef: React.RefObject<HTMLDivElement>;
    private inputElementRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();
    private optionsRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    static getDerivedStateFromProps(props: ICompactTimePickerProps, state: ICompactTimePickerState) {
        const { value, is12Hour = false, minTime = '', maxTime = '' } = props;
        const { time } = state;
        if (value || time) {
            if (value === '') { return { time: '' }; }

            const timeRange = minTime && maxTime ? isTimeOutOfRange(minTime, maxTime, time) : false;
            if (timeRange) {
                return { time: '' };
            }

            const timeSeperator = ':';
            const [hour, minute] = value && value.split(timeSeperator) || time.split(timeSeperator);
            const isValidTime = timeValidator.isValidTime(value || time, is12Hour, timeSeperator!);

            if (isValidTime) {
                return {
                    time: getTimeAsString({ hour, minute }, { is12Hour, timeSeperator })
                };
            }

            return {
                time: ''
            };
        }
        return null;
    }

    constructor(props: ICompactTimePickerProps) {
        super(props);
        this.rootElementRef = props.elementRef || React.createRef<HTMLDivElement>();
        this.state = getInitialState(props);
    }

    render() {
        const { disabled, className, hidden, placeholder, error, hideClearIcon, title, clearIconTitle = 'Clear Time' } = this.props;
        const { time } = this.state;
        return hidden ? null : (
            <Wrapper ref={this.rootElementRef} onClick={this._onClick} onKeyUp={this._onKeyUp}>
                <Textbox
                    placeholder={placeholder}
                    className={className}
                    error={error}
                    disabled={disabled}
                    elementRef={this.inputElementRef}
                    title={title}
                    clearIconTitle={clearIconTitle}
                    value={time}
                    iconName="ap-clock.outline"
                    iconPosition="right"
                    autoComplete="off"
                    allowToClear={!hideClearIcon}
                    onClear={this.resetCompactTimePicker}
                />
                {this.prepareCalloutAndListView()}
            </Wrapper>
        );
    }

    private prepareCalloutAndListView(): React.ReactElement {
        const { is12Hour, interval = 60, disabled } = this.props;
        const { isCallOutVisible } = this.state;

        return (
            <StyledCallout
                hidden={!isCallOutVisible || disabled}
                onDismiss={this.hideCallout}
                targetRef={this.rootElementRef}
                animateOnOpen={false}
                preventClickEventPropagation>
                <Typography.Small>
                    <StyledCalloutContent>
                        {this.preparePicker(getOptions(is12Hour, interval))}
                    </StyledCalloutContent>
                </Typography.Small>
            </StyledCallout>
        );
    }

    private _onClick = () => {
        const { isCallOutVisible } = this.state;

        if (isCallOutVisible) {
            this.onFocusListView();
            return;
        }

        this.setState({
            isCallOutVisible: true
        }, () => {
            this.onFocusListView();
        });

    }

    private onFocusListView() {
        const { current } = this.optionsRef;
        if (current) {
            current.focus();
        }
    }

    private resetCompactTimePicker = (e: React.MouseEvent) => {
        e.stopPropagation();
        this.setState({
            time: '',
            isCallOutVisible: false
        });
        this._onChange(['']);
    }

    private hideCallout = () => {
        this.setState({
            isCallOutVisible: false
        });
    }

    private preparePicker(items: string[]): React.ReactElement {
        const { minTime, maxTime, hideDisabledItems, interval = 60, is12Hour } = this.props;
        const { time } = this.state;
        return (
            <Picker
                items={items}
                elementRef={this.optionsRef}
                itemHeight={35}
                onChange={this._onChange}
                selectedKey={ensureArray(time)}
                interval={interval}
                is12Hour={is12Hour}
                minTime={minTime}
                maxTime={maxTime}
                hideDisabledItems={hideDisabledItems}
            />
        );
    }

    private _onChange = (selectedTime: string[]) => {
        const { time } = this.state;
        const { onChange } = this.props;
        const [firstValue] = selectedTime;
        const newTiming = selectedTime.length === 0 ? time : firstValue;

        this.setState({
            time: newTiming,
            isCallOutVisible: false
        });

        if (!onChange) {
            return;
        }
        onChange(newTiming);
    }

    private _onKeyUp = (ev: React.KeyboardEvent) => {
        switch (ev.keyCode) {
            case KEYS_TO_LISTEN['spaceBar']:
            case KEYS_TO_LISTEN['enter']:
                this.setState({
                    isCallOutVisible: true
                }, () => {
                    this.onFocusListView();
                });
                break;
            default: break;
        }
    }
}
